<script setup lang="ts">
import { ref, Ref, watch } from "vue";
import selectBox from "@/components/Common/selectBox.vue"
import BaseAutoCompleteWithSingleSelectedItem from "@/components/AutoCompletes/BaseAutoCompleteWithSingleSelectedItem.vue";
import AllowAccessOfficesEditList from "@/components/AllowAccessOfficeParts/AllowAccessOfficesEditList.vue"
import getOperationOffice from "@/types/responses/getOperationOffice"
import getAllowAccessOfficeResponse from "@/types/responses/getAllowAccessOffice"
import { useAuth } from "@/services/user/Auth0UserProvider"
import Api from "@/services/api/ApiServiceFabrick"
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import { SELECTABLE_STATUSES } from "@/services/consts/allow_access_office";
import i18n from '@/lang/i18n';

type keyValue = {
  key: string | number,
  value: string | number
}

const emits = defineEmits(['closeModal'])
const props = defineProps<{
  selectedAccount: any
  operationOffices: getOperationOffice[]
  modalState: boolean
  showFlash: (message: string, type: string, fadeout: boolean) => {value: any}
}>()

const title = i18n.global.t('pages.allowAccessOffice.title.edit')

const state: Ref<boolean> = ref(false)
const operationOffice: Ref<getOperationOffice | undefined> = ref()
const closeBtnName = i18n.global.t('pages.common.button.close')
const isLoading: Ref<boolean> = ref(false)
const AllowAccessOffices: Ref<getAllowAccessOfficeResponse[]> = ref([])
const filterAllowAccessOffices: Ref<getAllowAccessOfficeResponse[]> = ref([])
const createAuthority = ref(false)
const deleteAuthority = ref(false)
const selectedStatusKey: Ref<keyValue> = ref(SELECTABLE_STATUSES[0])
const { loginWithRedirect } = useAuth()

function getData() {
  getAllowAccessOffices()
  getAuthorityList()
}

function getAllowAccessOffices() {
  isLoading.value = true
  Api().getAllowAccessOffices().then((data: getAllowAccessOfficeResponse[]) => {
    AllowAccessOffices.value = data
    filterAllowAccessOffices.value = filterByAccountAllowAccessOffice(data)
    isLoading.value = false
  }).catch((error) => {
    return errorHandle(error.response)
  })
}

const getAuthorityList = async(isError = false) => {
  if (isError) await AuthorityCacheApi.reflesh()
  const authorityList = await AuthorityCacheApi.getWithParse()
  createAuthority.value = authorityList["octlink.allow_access_office.update"]
  deleteAuthority.value = authorityList["octlink.allow_access_office.update"]
}

function filterByAccountAllowAccessOffice(allowAccessOffices: getAllowAccessOfficeResponse[]) {
  return allowAccessOffices.filter((allowAccessOffice) => {
    return allowAccessOffice.account_id == props.selectedAccount.id
  })
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      state.value = false
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      state.value = false
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false)
      break
  }
  return state.value = false
}

function close() {
  operationOffice.value = undefined
  emits('closeModal', false)
}

function inactiveModal(isSucceeded: boolean) {
  if (!isSucceeded) return getAuthorityList(true)
  getAllowAccessOffices()
}

watch(() => props.modalState, (value: boolean) => {
  state.value = value
  if (state.value) {
    if (AllowAccessOffices.value.length) return filterAllowAccessOffices.value = filterByAccountAllowAccessOffice(AllowAccessOffices.value)
    getData()
  }
  selectedStatusKey.value = SELECTABLE_STATUSES[0]
})
</script>

<template>
  <v-dialog
    id="modal"
    v-model="state"
    width="50%"
    class="allow-access-office-modal"
    persistent
  >
    <template v-if="isLoading">
      <div class="list-loading h-100">
        <label>{{ i18n.global.t('pages.common.list.reading') }}</label>
      </div>
    </template>
    <v-card
      style="background-color: white"
      class="w-100"
    >
      <v-card-title class="fs-6 fw-bold grey lighten-2 mx-auto mt-1">
        {{ title }}
      </v-card-title>
      <v-card-text>
        <div class="w-100">
          {{ `${i18n.global.t('pages.allowAccessOffice.accountName')}: ${props.selectedAccount.last_name + props.selectedAccount.first_name}` }}
          <div class="d-flex justify-content-between mt-1">
            <div class="w-50">
              <BaseAutoCompleteWithSingleSelectedItem
                :items="props.operationOffices"
                v-model="operationOffice"
                :label="i18n.global.t('pages.allowAccessOffice.operationOffice')"
                itemName="name"
              />
            </div>
            <div class="d-flex align-end w-25">
              <selectBox
                v-model="selectedStatusKey"
                :items="SELECTABLE_STATUSES"
                :clearable="false"
              />
            </div>
          </div>
        </div>
        <div class="mx-auto my-2 w-100">
          <AllowAccessOfficesEditList
          :allowAccessOffices="filterAllowAccessOffices"
          :account="props.selectedAccount"
          :operationOffices="operationOffice ? [operationOffice] : props.operationOffices"
          :createAuthority="createAuthority"
          :deleteAuthority="deleteAuthority"
          :selectedStatusKey="selectedStatusKey"
          @inactiveModal="inactiveModal"
        />
        </div>
      </v-card-text>
      <div class="d-flex w-100 justify-content-end mb-4 pr-6">
        <v-btn class="close-btn" @click="close">
          {{ closeBtnName }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>
<style scooped>
.allow-access-office-modal .v-overlay__content {
  height: 100%;
}
.v-dialog .v-overlay__content > .v-card {
  min-height: 100%;
}
</style>
